import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Users } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import ApertureLoader from "@/components/ApertureLoader";
import ApplicantHeatmap from "@/components/applicants/ApplicantHeatmap";
import { api } from "@/lib/api";

/**
 * Roster Heatmap — the same visualization powering the applicant pipeline
 * map, dedicated here to *rostered* photographers only (i.e. everyone who
 * has completed the application → interview → onboarding funnel and been
 * approved by Reelty to receive Property Shoots Hub assignments).
 *
 * Data comes from `GET /api/admin/roster-map` which already reshapes
 * photographers into the exact structure ApplicantHeatmap consumes
 * (id, name, stage, score, latitude, longitude, region, resolved_city,
 * zip_code, desired_travel_distance, created_at, resume, notes).
 */
export default function RosterHeatmapPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ photographers: [], stages: [], tabs: [] });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/roster-map");
        setData(data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to load roster map.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Every rostered stage is enabled by default — admins toggle from the
  // heatmap's built-in filter chips if they want to narrow it down.
  const activeStages = useMemo(
    () => new Set(data.stages || []),
    [data.stages],
  );

  if (loading) return <ApertureLoader label="Loading roster map…" />;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8 md:py-10" data-testid="roster-heatmap-page">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 font-mono-tag text-[10px] tracking-[0.25em] text-[#B08D57] mb-2">
            <Users className="w-3.5 h-3.5" strokeWidth={2.25} />
            APPROVED ROSTER · GEOGRAPHIC REACH
          </div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tighter leading-[1.05] text-[#1A0F0A]">
            Photographer roster heatmap
          </h1>
          <p className="text-sm md:text-base text-[#4D3F35] mt-3 max-w-2xl">
            Where every onboarded photographer lives, colored on the same
            coverage tiers as the applicant map. Click into a metro popup to
            open a photographer's profile card.
          </p>
        </div>
        <Button
          asChild
          variant="ghost"
          className="border border-[#1A0F0A]/15 hover:bg-[#1A0F0A]/5 text-[#1A0F0A] font-bold tracking-wide rounded-sm"
          data-testid="roster-heatmap-back"
        >
          <Link to="/admin">
            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={2} />
            Back to roster
          </Link>
        </Button>
      </div>

      <ApplicantHeatmap
        applicants={data.photographers || []}
        activeStages={activeStages}
        entity="photographer"
      />
    </div>
  );
}
